/**
 * Rule Display Component
 * Displays a single rule with its conditions and actions
 */

import React, { useState } from 'react';
import { OrganizationRule, RULE_FIELD_OPTIONS, RULE_OPERATOR_OPTIONS } from '../../types/organization';
import { useApplyRule, useDeleteRule } from '../../hooks/useOrganization';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './RuleDisplay.css';

interface RuleDisplayProps {
  rule: OrganizationRule;
  onEdit: (rule: OrganizationRule) => void;
}

export const RuleDisplay: React.FC<RuleDisplayProps> = ({ rule, onEdit }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const applyRule = useApplyRule();
  const deleteRule = useDeleteRule();

  const handleRefresh = () => {
    applyRule.mutate(
      { listId: rule.list_id, ruleId: rule.rule_id },
      {
        onSuccess: (response) => {
          // Show toast or notification
          console.log(`Added ${response.ads_added} ads to list`);
        },
        onError: (error) => {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          alert(`Failed to apply rule: ${errorMessage}`);
        },
      }
    );
  };

  const handleDelete = () => {
    deleteRule.mutate(
      { listId: rule.list_id, ruleId: rule.rule_id },
      {
        onSuccess: () => {
          setShowDeleteConfirm(false);
        },
        onError: (error) => {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          alert(`Failed to delete rule: ${errorMessage}`);
        },
      }
    );
  };

  const getFieldLabel = (fieldValue: string) => {
    const field = RULE_FIELD_OPTIONS.find(f => f.value === fieldValue);
    return field ? field.label : fieldValue;
  };

  const getOperatorLabel = (operatorValue: string) => {
    const operator = RULE_OPERATOR_OPTIONS.find(op => op.value === operatorValue);
    return operator ? operator.label : operatorValue;
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="rule-display">
      <div className="rule-header">
        <div className="rule-name">{rule.rule_name}</div>
        <div className="rule-actions">
          <button
            className="rule-action-btn refresh-btn"
            onClick={handleRefresh}
            disabled={applyRule.isPending}
            title="Refresh - Apply rule to all ads"
          >
            <RefreshIcon fontSize="small" />
            {applyRule.isPending ? 'Applying...' : 'Refresh'}
          </button>
          <button
            className="rule-action-btn edit-btn"
            onClick={() => onEdit(rule)}
            title="Edit rule"
          >
            <EditIcon fontSize="small" />
          </button>
          <button
            className="rule-action-btn delete-btn"
            onClick={() => setShowDeleteConfirm(true)}
            title="Delete rule"
          >
            <DeleteIcon fontSize="small" />
          </button>
        </div>
      </div>

      <div className="rule-conditions">
        {rule.conditions.map((condition, index) => (
          <div key={index} className="condition-chip">
            <span className="condition-field">{getFieldLabel(condition.field)}</span>
            <span className="condition-operator">{getOperatorLabel(condition.operator)}</span>
            {condition.value && (
              <span className="condition-value">&quot;{condition.value}&quot;</span>
            )}
            {index < rule.conditions.length - 1 && (
              <span className="condition-and">AND</span>
            )}
          </div>
        ))}
      </div>

      {rule.last_applied_at && (
        <div className="rule-stats">
          <CheckCircleIcon fontSize="small" className="stats-icon" />
          Last applied: <strong>{rule.last_applied_count || 0}</strong> ads added
          {' '}{formatTimestamp(rule.last_applied_at)}
        </div>
      )}

      {showDeleteConfirm && (
        <div className="delete-confirm">
          <div className="delete-confirm-message">
            Delete rule &quot;{rule.rule_name}&quot;? This will not remove ads from the list.
          </div>
          <div className="delete-confirm-actions">
            <button
              className="btn-cancel"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteRule.isPending}
            >
              Cancel
            </button>
            <button
              className="btn-delete"
              onClick={handleDelete}
              disabled={deleteRule.isPending}
            >
              {deleteRule.isPending ? 'Deleting...' : 'Delete Rule'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

